/*
 * Model for a single Topic
 */

define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
  "modules/topicsSplit/topic/topicScheduledStatus/topicScheduledStatusModel",
  "modules/topicsSplit/topic/topicExamples/topicExamplesModel",
  "modules/topicsSplit/topic/topicConversation/topicConversationModel",
  "modules/topicsSplit/topic/topicLabel/topicLabelModel",
  "modules/shared/history/historyModel",
  "modules/tasks/tasksModel",
  "modules/topicsSplit/topic/touchPointLabel/touchPointLabelModel",
  "modules/accountAdmin/kbAdmin/kbModel",
], function (
  Backbone,
  $,
  _,
  Messenger,
  Properties,
  SortableListModel,
  TopicScheduledStatusModel,
  TopicExamplesModel,
  TopicConversationModel,
  TopicLabelModel,
  HistoryModel,
  TasksModel,
  TouchPointLabelModel,
  KbModel,
) {
  "use strict";

  const TopicModel = {};

  TopicModel.actionTypes = {
    DISABLED_RESPONSE: "Disabled Response",
    PUBLISHED_RESPONSE: "Published Response",
    ENABLED_RESPONSE: "Enabled Response",
  };

  // Cannot use this.constructor.__super__ because it does not seem to work correctly
  // when another model subclasses this model (like QuickCreateModel.quickCreateTopic),
  // which can lead to an infinite loop.
  const TopicModelSuper = Backbone.Model.prototype;

  TopicModel.topic = Backbone.Model.extend({
    idAttribute: "intentId",
    statusValue: "", //extension for the status name
    contextValue: "", //extension for the context name
    archived: false,
    defaults: function () {
      const defaultStatusId = Messenger.reqres.request("default:topic:status");
      const defaultContextId = Messenger.reqres.request("default:topic:context");
      return {
        intentName: "",
        intentDescription: "",
        isQualifiable: true,
        statusId: defaultStatusId,
        contextId: defaultContextId,
      };
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      const url = Properties.rootURL + "/" + kbId + "/intents/";
      if (this.id) {
        return url + this.id;
      }
      return url;
    },
    initializeCustom: function () {
      //when the textbox changes the intentName attribute
      this.on(
        "enter:intentName",
        function () {
          //change the description in the conversation model if it exists
          if (this.conversationModel) {
            this.conversation().set("description", this.get("intentName"));
          }
          //if the topic is new and topic label is nonexistent or empty, set the topic label of the response language to
          // match the title
          if (this.isNew()) {
            const responseLanguage = Messenger.reqres.request("default:response:language"); //get default response
            // language
            const labelMatch = this.topicLabels().findWhere({ languageId: responseLanguage });
            if (this.topicLabels().length === 0 || !labelMatch) {
              const newLabel = this.topicLabels().addLabel(responseLanguage);
              newLabel.set("summary", this.get("intentName"));
            } else if (labelMatch.get("summary").length === 0) {
              labelMatch.set("summary", this.get("intentName"));
            }
            const kbModel = Messenger.reqres.request("active:kb:model");
            if (
              kbModel.get("engineMode") === KbModel.ENGINE_MODE.KEYWORD &&
              this.examples().length === 0
            ) {
              const example = this.examples().addExample();
              let exampleEntry = example.entries().at(0);
              if (!exampleEntry) {
                exampleEntry = example.entries().addEntry(0);
              }
              exampleEntry.set("surfaceForm", this.get("intentName"));
              this.examples().sort();
            }
          }
        },
        this,
      );

      this.on(
        "enter:intentName:locale",
        function (localeId) {
          //change the description in the conversation model if it exists
          if (this.conversationModel) {
            this.conversation().set("description", this.get("intentName"));
          }
          //if the topic is new and topic label is nonexistent or empty, set the topic label of the response language to
          // match the title
          if (this.isNew()) {
            const labelMatch = this.topicLabels().findWhere({ languageId: localeId });
            if (this.topicLabels().length === 0 || !labelMatch) {
              const newLabel = this.topicLabels().addLabel(localeId);
              newLabel.set("summary", this.get("intentName"));
            } else if (labelMatch.get("summary").length === 0) {
              labelMatch.set("summary", this.get("intentName"));
            }
          }
        },
        this,
      );

      this.on("change:statusId", () => this._updateStatusValue());

      this.on("change:contextId", () => this._updateContextValue());

      //set the context value and status value based on the corresponding Ids that have already been set
      if (this.get("statusId") > 0) {
        this._updateStatusValue();
      }
      if (this.get("contextId") > 0) {
        this._updateContextValue();
      }
    },
    conversation: function () {
      this.conversationModel = this.getSubModel(
        this.conversationModel,
        TopicConversationModel.topicConversation,
        "/conversation",
        false,
      );
      this.conversationModel.on("fetch", this._syncConversationStatus, this);
      this.conversationModel.on("sync", this._syncConversationStatus, this);
      this.conversationModel.on("change:statusId", this._syncConversationStatus, this);
      return this.conversationModel;
    },
    examples: function () {
      this.examplesCol = this.getSubModel(
        this.examplesCol,
        TopicExamplesModel.topicExamples,
        "/examples",
        true,
      );
      return this.examplesCol;
    },

    history: function (transactionId) {
      const history = new HistoryModel.historyCollection();
      if (!this.isNew()) {
        const url = this.get("self").href + "/history/" + transactionId;
        history.url = function () {
          return url;
        };
        history.fetch();
      }
      return history;
    },

    /*
     * Gets the live Conversation model.  Has a special boolean parameter called fetch.
     * If true, the function will try to fetch the live conversation from the server.
     * If false, the function will just return an unsynced, new live Conversation model.
     * This is useful for when we need the model to do a publish, but don't need it for data and
     * don't want to do a worthless GET request
     */
    liveConversation: function (fetch) {
      if (!this.liveConversationModel) {
        //if there is no existing live conversation model
        //get the conversation to pass to the live conversation.
        const conversation = this.conversation();
        //create a new live conversation model and add it to this model's subModel array
        this.liveConversationModel = new TopicConversationModel.liveTopicConversation();
        //add model to sub models array
        if (!this.subModels) {
          this.subModels = [];
        }
        this.subModels.push(this.liveConversationModel);
        this.liveConversationModel.workingConversation = conversation; //pass working response to live conversation
        // object
      }
      if (
        fetch &&
        !(this.liveConversationModel.isRequested || this.liveConversationModel.isSynced)
      ) {
        //don't bother fetching an already synced or requested conversation
        if (!this.isNew()) {
          //load from server
          this.liveConversationModel.url = _.result(this, "url") + "/conversation/live"; //underscore result function
          // works whether url is an
          // property or function of the
          // model
          this.liveConversationModel.fetch();
        }
      }

      return this.liveConversationModel;
    },
    move: function (groupModel) {
      //reset topic group attribute
      this.topicGroupModel = groupModel;
      const groupId = groupModel.get("groupId");
      //update parentGroupId (must be done after topicGroupModel is reassigned because this change triggers the
      // topicFolder module to re-render
      this.set("parentGroupId", groupId);
      //move the topic to the group with the given ID
      //make the URL to move the topic
      const kbId = Messenger.reqres.request("active:kb");
      const moveUrl = Properties.rootURL + "/" + kbId + "/intents/groups/" + groupId + "/intents";
      //disconnect from the collection (for some reason this doesn't always happen...)
      if (this.collection) {
        this.collection._removeReference(this);
      }
      //if the topic is new, then all we need to do is change the current URL to moveURL, so that the topic is
      // eventually saved to the correct folder. If the topic exists, then we need to make a PUT request to the server
      // to move the topic.
      if (this.isNew()) {
        this.url = moveUrl;
      } else {
        //update the topic
        this.save(null, { url: moveUrl, validate: false });
      }
    },
    getStatus: function () {
      let statusId;
      if (this.conversationModel && this.conversationModel.isSynced) {
        statusId = this.conversationModel.get("statusId");
      }
      if (!statusId) {
        statusId = this.get("statusId");
      }
      if (!statusId) {
        statusId = Messenger.reqres.request("default:topic:status");
      }
      return statusId;
    },
    setStatus: function (statusId) {
      this.set("statusId", statusId);
      if (this.conversationModel && this.conversationModel.isSynced) {
        this.conversationModel.set("statusId", statusId);
      }
    },
    scheduledStatus: function () {
      // DO NOT store this as a sub-model, it needs to be loaded and saved separately
      if (!this.scheduledStatusModel) {
        // Do use the logic already in place in getSubModel(), just remove the new model from the
        // subModels array using splice()
        this.scheduledStatusModel = this.getSubModel(
          this.scheduledStatusModel,
          TopicScheduledStatusModel.topicScheduledStatus,
          "/status/scheduled",
          false,
        );
        const modelIndex = this.subModels.indexOf(this.scheduledStatusModel);
        this.subModels.splice(modelIndex, 1);
      }
      return this.scheduledStatusModel;
    },
    tasks: function () {
      const kbId = Messenger.reqres.request("active:kb");
      const url = Properties.rootURL + "/" + kbId + "/tasks/intent/" + this.get("intentId");
      this.tasksCollection = this.getSubModel(this.tasksCollection, TasksModel.tasks, url, false);
      return this.tasksCollection;
    },
    topicGroup: function () {
      if (!this.topicGroupModel) {
        //get the topic folder of this topic from the collection it is in (or fetch it if the topic isn't part of a
        // collection or the collection is from search results)
        if (this.collection && this.collection.topicGroup) {
          this.topicGroupModel = this.collection.topicGroup;
        } else {
          //fetch the folder
          this.topicGroupModel = Messenger.reqres.request("topic:group", this.get("parentGroupId"));
        }
      }
      return this.topicGroupModel;
    },
    topicLabels: function () {
      this.topicLabelCol = this.getSubModel(
        this.topicLabelCol,
        TopicLabelModel.topicLabels,
        "/summaries",
        false,
      );
      return this.topicLabelCol;
    },
    touchPointLabels: function () {
      this.touchPointTopicLabelCol = this.getSubModel(
        this.touchPointTopicLabelCol,
        TouchPointLabelModel.touchPointLabels,
        "/summariesTouchpoint",
        false,
      );
      return this.touchPointTopicLabelCol;
    },
    saveChildren: function (options) {
      //pass all the data needed to the child models for saving
      //only call child models that already have been loaded (no need to fetch models just to save them again)
      if (this.examplesCol) {
        this.examples().intentId = this.get("intentId");
        this.examples().url = this.get("intentExamples").href;
      }
      if (this.topicLabelCol) {
        this.topicLabels().url = this.get("intentSummaries").href;
      }
      if (this.touchPointTopicLabelCol) {
        this.touchPointLabels().url = this.get("intentSummariesTouchpoint").href;
      }
      if (this.scheduledStatusModel) {
        this.scheduledStatusModel.url = this.get("scheduledStatus").href;
      }
      if (this.conversationModel) {
        this.conversationModel.set("intentId", this.get("intentId"));
        this.conversationModel.url = this.get("conversation").href;
      }
      if (this.tasksCollection) {
        //Remove any tasks that aren't initialized (no ID and no due date).  We should not try to save those; they are
        //either the default for a topic without any tasks, or ones that were canceled/deleted before saving
        for (let i = 0; i < this.tasksCollection.models.length; i++) {
          const model = this.tasksCollection.at(i);
          if (model.get("taskId") === undefined && model.get("dueDate") === 0) {
            this.tasksCollection.remove(model);
            i--;
          }
          //if it wasn't removed, make sure it has the correct intent ID.  This is especially important for new
          // topics, which didn't have an intent ID
          //when any created-along-with-the-intent tasks were created.
          else if (this.tasksCollection.at(i).get("intentId") === undefined) {
            this.tasksCollection.at(i).set("intentId", this.get("intentId"));
          }
        }
      }
      //save all the child models and collections of the topic
      this.saveSubModels(options);
    },
    _validateDuplicateIntentName: function (value) {
      if (this.collection) {
        const lowerValue = value.toLowerCase();
        for (let i = 0; i < this.collection.length; ++i) {
          const entry = this.collection.at(i);
          if (entry !== this) {
            if (entry.get("intentName").toLowerCase() === lowerValue) {
              return $.t("topic.duplicateNameError");
            }
          }
        }
      }
    },
    determineValidateAttributes: function (attributes, options) {
      const attributesEmpty = !attributes || _.isEmpty(attributes);
      if (options && options.topLevelOnly) {
        // Do not validate the examples if only the Topic is being saved
        if (attributesEmpty) {
          return ["intentName", "intentDescription"];
        }
        // When the server sends back a new Topic, it includes an intentExamples property
        // that is a link to the examples.  If that is left in the validation attributes,
        // then the Topic fails to properly be filled in due to validation of intentExamples.
        if (attributes.intentExamples && attributes.intentExamples.href) {
          const attributesWithoutExamples = _.extend({}, attributes);
          delete attributesWithoutExamples.intentExamples;
          return attributesWithoutExamples;
        }
        return attributes;
      }
      if (attributesEmpty) {
        return ["intentName", "intentDescription", "intentExamples"];
      }
      // Make sure that intent examples are included in what is validated
      if (_.isObject(attributes) && !("intentExamples" in attributes)) {
        return _.extend(
          {
            intentExamples: true,
          },
          attributes,
        );
      }
      if (_.isArray(attributes) && !attributes.contains("intentExamples")) {
        return ["intentExamples"].concat(attributes);
      }
      return attributes;
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "intentName") {
        return (
          this.validateRequiredValue(value, { maxLength: 128 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateIntentName(value)
        );
      }
      if (attribute === "intentDescription") {
        return this.validateStringLength(value, { maxLength: 1000 });
      }
      if (attribute === "intentExamples") {
        // Validate that the topic has at least one example, only if the example collection exists.
        // Must only do this if the example collection exists so that a new "empty" topic can be created when
        // necessary before opening the Topic editor (like when a Task creates a new topic)
        if (this.examplesCol && this.examplesCol.length === 0 && !this.examplesCol.saveToAdd) {
          return $.t("topic.examples.noExamplesError");
        }
      }
    },
    _discardSubModels: function () {
      delete this.conversationModel;
      delete this.examplesCol;
      delete this.liveConversationModel;
      delete this.scheduledStatusModel;
      delete this.tasksCollection;
      delete this.topicLabelCol;
      delete this.touchPointTopicLabelCol;
      return TopicModelSuper._discardSubModels.apply(this, arguments);
    },
    _syncConversationStatus: function () {
      // Do not allow running the logic that will mark the conversation as needing to be published, if the
      // status is being updated based on the conversation.
      this.set("statusId", this.conversationModel.get("statusId"), { silent: true });
      // Also update the status value
      this._updateStatusValue();
    },
    _updateContextValue: function () {
      const allContexts = Messenger.reqres.request("kb:contexts");
      allContexts.onSynced(function () {
        //do not let the change dirty the topic, but do have it fire a change event
        const isDirty = this.dirty;
        const context = allContexts.findWhere({ id: this.get("contextId") });
        if (context != null) {
          this.contextValue = context.get("status");
        } else {
          this.contextValue = "";
        }
        this.trigger("change");
        this.dirty = isDirty;
      }, this);
    },
    _updateStatusValue: function () {
      const allStatuses = Messenger.reqres.request("kb:statuses");
      allStatuses.onSynced(function () {
        const statusArray = allStatuses.get("allTopicStatuses");
        const statusId = this.getStatus();
        for (let i = 0; i < statusArray.length; i++) {
          if (statusArray[i].statusId === statusId) {
            if (this.statusValue != statusArray[i].statusName) {
              //do not let the change dirty the topic, but do have it fire a change event
              const isDirty = this.dirty;
              this.statusValue = statusArray[i].statusName;
              this.trigger("change");
              this.dirty = isDirty;
            }
            break;
          }
        }
      }, this);
    },
  });

  TopicModel.decisionTopic = TopicModel.topic.extend({
    //a measure of how many times the decision topic has been added to a conversation since fetching/creation.
    //Only used on new decision topics to see if it is in use and should be saved.
    timesInUse: 0,
    defaults: {
      intentName: "",
      intentDescription: "",
      isQualifiable: true,
      isDecisionIntent: true,
      statusId: 0,
      contextId: 0,
    },
    url: function () {
      if (!this.isNew()) {
        return this.get("self").href;
      } else {
        const kbId = Messenger.reqres.request("active:kb");
        return Properties.rootURL + "/" + kbId + "/intents/decisions/groups/0/intents"; //default to saving to the root
        // decisions folder
      }
    },
    _allowSave: function () {
      // Modify conditions to save, must be not new and dirty or new and used at least once (timesInUse > 0)
      // This is probably overkill, since I don't see when the first condition will ever be met...
      return (this.dirty && !this.isNew()) || (this.isNew() && this.timesInUse > 0);
    },
    saveChildren: function (options) {
      //pass all the data needed to the child models for saving (checking if the objects exist before trying to pass
      // the data)
      if (this.examplesCol) {
        this.examples().intentId = this.get("intentId");
        this.examples().url = this.get("intentExamples").href;
      }
      if (this.topicLabelCol) {
        this.topicLabels().url = this.get("intentSummaries").href;
      }
      if (this.touchPointTopicLabelCol) {
        this.touchPointLabels().url = this.get("intentSummariesTouchpoint").href;
      }
      //save all the child models and collections of the topic
      this.saveSubModels(options);
    },
  });

  TopicModel.archiveTopic = Backbone.Model.extend({
    url: function () {
      const intentId = this.get("intentId");
      if (!intentId && typeof intentId != "number") {
        throw new Error("Cannot determine topic archive URL, topic ID is not valid");
      }
      const kbId = Messenger.reqres.request("active:kb");
      return `${Properties.rootURL}/${kbId}/intents/${intentId}/archive`;
    },
  });

  TopicModel.restoreTopic = Backbone.Model.extend({
    url: function () {
      const intentId = this.get("intentId");
      if (!intentId && typeof intentId != "number") {
        throw new Error("Cannot determine topic restore URL, topic ID is not valid");
      }
      const kbId = Messenger.reqres.request("active:kb");
      return `${Properties.rootURL}/${kbId}/intents/${intentId}/restore`;
    },
  });

  //Request to create a new decision topic with the name provided, including a matching example
  //helps prevent a circular dependency with the topicConversation object in requireJS
  Messenger.reqres.setHandler(
    "new:topic:decision",
    function (label) {
      const newDecision = new TopicModel.decisionTopic({ intentName: label });
      //create example
      const newExample = newDecision.examples().addExample();
      const newExampleEntries = newExample.entries();
      let entry = newExampleEntries.at(0); //get first entry to edit
      //split label by spaces
      const labelEntries = label.split(/\s+/);
      for (let i = 0; i < labelEntries.length; i++) {
        if (i > 0) {
          //create a new entry if needed
          entry = newExampleEntries.addEntry(i);
        }
        entry.set("surfaceForm", labelEntries[i]);
      }
      //we no longer need to update the example separately, since RD-963
      //create an English topic label using the provided label parameter
      const decLabels = newDecision.topicLabels();
      const decLabel = decLabels.addLabel(1); //english has id = 1
      decLabel.set("summary", label);
      return newDecision;
    },
    this,
  );

  //Request for a specific topic (intent)
  Messenger.reqres.setHandler("fetch:topic", function (intentId) {
    const topic = new TopicModel.topic({ intentId: intentId });
    topic.fetch();
    return topic;
  });

  //Request for the headers of the topic examples history list
  Messenger.reqres.setHandler("topic:history:headers", function (headerType) {
    if (headerType === "detailModal") {
      return new SortableListModel.headerCollection([
        {
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          textField: true,
        },
        {
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          textField: true,
        },
      ]);
    } else if (headerType === "detailModalHtml") {
      return new SortableListModel.headerCollection([
        // The values can include HTML, and look very bad if they are escaped.
        // So, make sure to allow HTML to be rendered in the columns using
        // escapeHTML: false
        {
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          textField: true,
          escapeHTML: false,
        },
        {
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          textField: true,
          escapeHTML: false,
        },
      ]);
    } else {
      const allowFiltering = headerType !== "lazyLoading";
      const allowSorting = headerType !== "lazyLoading";
      return new SortableListModel.headerCollection([
        {
          headerName: $.t("history.user"),
          modelAttribute: "userName",
          sortable: allowSorting,
          filterable: allowFiltering,
          textField: true,
          width: "10%",
        },
        {
          headerName: $.t("history.date"),
          modelAttribute: "dateTime",
          defaultSort: true,
          defaultReversed: true,
          sortable: allowSorting,
          dateField: true,
          width: "10%",
        },
        {
          headerName: $.t("history.change"),
          modelAttribute: "changeType",
          sortable: allowSorting,
          filterable: allowFiltering,
          textField: true,
          width: "10%",
        },
        {
          headerName: $.t("history.languageName"),
          modelAttribute: "languageName",
          hideable: true,
          hidden: false,
          sortable: allowSorting,
          filterable: allowFiltering,
          textField: true,
          width: "10%",
        },
        {
          headerName: $.t("history.touchpointName"),
          modelAttribute: "touchpointName",
          hideable: true,
          hidden: false,
          sortable: allowSorting,
          filterable: allowFiltering,
          textField: true,
          width: "10%",
        },
        // The values can include HTML, and look very bad if they are escaped.
        // So, make sure to allow HTML to be rendered in the columns using
        // escapeHTML: false
        {
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          sortable: allowSorting,
          textField: true,
          escapeHTML: false,
          width: "25%",
        },
        {
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          sortable: allowSorting,
          textField: true,
          escapeHTML: false,
          width: "25%",
        },
      ]);
    }
  });
  TopicModel.pagedHistoryCollection = HistoryModel.historyCollection.extend({
    initialize: function (options) {
      this.intentId = options.intentId;
    },
    initDataCollection: function () {
      if (!this.dataCollection) {
        this.dataCollection = new Backbone.Collection();
        this.dataCollection.intentId = this.intentId;
      }
    },
    getDataCollection: function () {
      return this.dataCollection;
    },
    pagination: function (currentPage, recordsPerPage) {
      if (!this.dataCollection || this.dataCollection.length === 0) {
        this.initDataCollection();
        this.historyCount();
      }
      // Construct the URL for the fetch request
      const kbId = Messenger.reqres.request("active:kb");
      const url = `${Properties.rootURL}/${kbId}/intents/${this.intentId}/history/${currentPage}/${recordsPerPage}`;
      this.dataCollection.url = () => url;
      // Return a new promise that resolves with the collection
      return new Promise((resolve, reject) => {
        this.dataCollection.fetch({
          success: () => {
            resolve(this.dataCollection);
          },
          error: (_collection, response) => {
            reject(Messenger.getErrorFromResponse(response));
          },
        });
      });
    },
    historyCount: function () {
      if (this.countModel) {
        return this.countModel;
      }
      this.countModel = new Backbone.Model();
      const kbId = Messenger.reqres.request("active:kb");
      const url = `${Properties.rootURL}/${kbId}/intents/${this.intentId}/historyCount`;
      this.countModel.url = () => url;

      // Fetch the count value from the server
      this.countModel.fetch();

      return this.countModel;
    },
  });

  return TopicModel;
});
